import React, {
    createContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react"

export interface BreakpointConfig {
    [name: string]: number
}

export interface BreakpointContextValue {
    breakpoints: BreakpointConfig
    currentBreakpoint: string
}

interface BreakpointsProviderProps {
    breakpoints: BreakpointConfig
    breakpointUnit?: "px" | "em"
    children: ReactNode
}

function getCurrentBreakpoint(
    breakpoints: BreakpointConfig,
    width: number,
): string {
    const sorted = Object.entries(breakpoints).sort((a, b) => b[1] - a[1])
    const matched = sorted.find(([, value]) => width >= value)
    return matched?.[0] ?? sorted.at(-1)?.[0] ?? ""
}

const BreakpointsContext = createContext<BreakpointContextValue>({
    breakpoints: {},
    currentBreakpoint: "",
})

export function BreakpointsProvider({
    breakpoints,
    children,
}: BreakpointsProviderProps) {
    const [currentBreakpoint, setCurrentBreakpoint] = useState(() =>
        getCurrentBreakpoint(
            breakpoints,
            typeof window === "undefined"
                ? Number.MAX_SAFE_INTEGER
                : window.innerWidth,
        ),
    )

    useEffect(() => {
        const updateBreakpoint = () => {
            setCurrentBreakpoint(
                getCurrentBreakpoint(breakpoints, window.innerWidth),
            )
        }

        updateBreakpoint()
        window.addEventListener("resize", updateBreakpoint)
        return () => window.removeEventListener("resize", updateBreakpoint)
    }, [breakpoints])

    const value = useMemo(
        () => ({ breakpoints, currentBreakpoint }),
        [breakpoints, currentBreakpoint],
    )

    return (
        <BreakpointsContext.Provider value={value}>
            {children}
        </BreakpointsContext.Provider>
    )
}

interface MediaProps {
    children(context: BreakpointContextValue): ReactNode
}

export function Media({ children }: MediaProps) {
    return <BreakpointsContext.Consumer>{children}</BreakpointsContext.Consumer>
}

export default BreakpointsContext
